/******************************************
*  Author : Riccardo Di Dato   
*  Created On : Wed Jul 06 2022
*  File : PushNotificationService.ts
*******************************************/

import { DetectedWebView } from "@ciaopeople/cp-shatsu/ClientDetection";
import { SimpleEventHandler } from "@ciaopeople/cp-shatsu/EventHandler";
import { Logger } from "@ciaopeople/cp-shatsu/Logging";
//import { getDeviceNameFromDetectedWebview, getNotificationDeviceTypeFromDetectedWebview, NotificationSystemType, SubscriptionIdentifier, SubscriptionListResponse } from "../api";
import { notificationDeviceInfoToDeviceSubscription } from "../nativeapps/CpNativeAppIntegration.js";
import { ClientSdkCorePublic } from "../sdk/ClientSdkPublicInterface.js";
import { PushNotificationServiceConfig } from "./PushNotificationServiceConfig.js";

import { isSystemSubscriptionWrapper, PushServiceInterface, SubscriptionStatus, SystemSubscriptionWrapper } from "./PushServiceInterface.js";
import { getDeviceNameFromDetectedWebview, getNotificationDeviceTypeFromDetectedWebview, NotificationSystemType, SubscriptionIdentifier, SubscriptionListResponse } from "@api-commons/api/types/SubscriptionTypes.js";

const SUBSCRIPTION_CHANGE_EVT = "subscriptionChanged";


export class PushNotificationService extends SimpleEventHandler implements PushServiceInterface {
    private detectedWebview: DetectedWebView;

    constructor(
        private sdk: ClientSdkCorePublic,
        private config: PushNotificationServiceConfig,
        private logger: Logger,
        registerListeners: boolean
    ) {
        super();
        this.detectedWebview = this.sdk.webviewDetect();

        if(!this.detectedWebview.native_app && window && window.Notification && registerListeners) {
            this.addListeners();
        }
    }
    public onSubscriptionStatusChange(cb: (x: SystemSubscriptionWrapper) => any): void {
        this.on(SUBSCRIPTION_CHANGE_EVT, (evt, data) => {
            if(isSystemSubscriptionWrapper(data)) {
                cb(data);
            }
            else cb(null);
        });
    }

    protected triggerSubscriptionStatusChange(x: SystemSubscriptionWrapper): void {
        this.trigger(SUBSCRIPTION_CHANGE_EVT, x);
    }

    private getFirebaseModule() {
        return import( /* webpackChunkName: "common/sdk/firebasePushNot" */ './FirebaseModule.js');
    }

    private getFirebaseToken(): Promise<string> {
        return Promise.all([
            this.getFirebaseModule(),
            this.getServiceWorkerRegistration()
        ]).then(
            ([
                fbMod,
                swReg
            ]) => fbMod.getFirebaseToken(this.config, swReg)
        );
    }
    /**
     * Vertifica che siamo in app nativa
     */
    private hasNativeAppGateway(): boolean {
        return this.detectedWebview.browser && this.detectedWebview.browser.native_app;
    }

    /**
     * Verifica che il browser supporti le notifiche push safari
     */
    private supportsSafariPushNotifications(): boolean {
        return 'safari' in window && 'pushNotification' in (window['safari'] as any);
    }

    private isBrowserEligibleForPushNotifications(): boolean {
        return !this.detectedWebview.browser.safari && ("serviceWorker" in navigator) && !this.detectedWebview.browser.webview;
    }

    private isSafariWithWebpushSupport(): boolean {
        return this.detectedWebview.browser.safari && this.detectedWebview.extra && this.detectedWebview.extra.safariVersion && this.detectedWebview.extra.safariVersion.major >= 16;
    }

    /**
     * Restituisce se il sistema in uso è compatibile.
     */
    public isSystemEnabled(): boolean {
        return this.hasNativeAppGateway() || this.isBrowserEligibleForPushNotifications() || this.isSafariWithWebpushSupport();
    }

    /**
     * Restituisce true se il sistema in uso è compatibile ma richiede una azione manuale per richiedere l'iscrizione 
     * alle notifiche 
     */
    isSubscriptionManualActivationContext(): boolean {
        return this.isSafariWithWebpushSupport();
    }

    private swReg = null;
    private getServiceWorkerRegistration(): Promise<ServiceWorkerRegistration> {
        if (this.swReg === null) {
            this.swReg = navigator.serviceWorker?.register(this.config.workerUrl)
                .then( (swReg) => {
                    this.logger.debug('Service Worker is registered', swReg);
                    
                    swReg.update();
                    return swReg;
                })
                .catch((error) => {
                    this.logger.error('Service Worker Error', error);
                });
        }
        return this.swReg;
    }

    

    getSubscriptionsAndCheckCurrentDevice(): Promise<SystemSubscriptionWrapper> {
        return this.listSubscribedDevices().then(res => {
            const devices = res.subscriptions;
            if (this.hasNativeAppGateway()) {
                return this.sdk.nativeapp.getNotificationDeviceInfo().then(
                    deviceInfo => {
                        const devSub = (deviceInfo.status == SubscriptionStatus.ENABLED?devices.find(e => e.token == deviceInfo.device.token):null);
                        return {
                            subscriptionStatus: deviceInfo.status,
                            device: devSub,
                            listsAlsoOtherDevices: true,
                            otherDevices: devSub?devices.filter(e => e.token != devSub.token):devices
                        };
                    }
                );
            }
            else {
                let status = SubscriptionStatus.NEVER_ASKED;
                if(this.supportsSafariPushNotifications()) status = SubscriptionStatus.BLOCKED;
                else if (window.Notification && window.Notification.permission == "granted") status = SubscriptionStatus.ENABLED;
                else if (window.Notification && window.Notification.permission == "default") status = SubscriptionStatus.NEVER_ASKED;
                else SubscriptionStatus.BLOCKED;

                return this.sdk.retrieveOrGenerateDeviceId().then(device_id => {
                    const baseData: SystemSubscriptionWrapper = {
                        subscriptionStatus: status,
                        listsAlsoOtherDevices: true
                    };

                    const foundDevice =
                        devices.find(e => e.deviceId == device_id.deviceId);

                    if(foundDevice) baseData.device = foundDevice;
                    baseData.otherDevices = devices.filter(e => e.deviceId != device_id.deviceId);

                    return baseData;
                    
                });

            }
        });
    }


    loadServiceWorkerAndGetSubscription(): Promise<SystemSubscriptionWrapper> {
        if (this.isSystemEnabled()) {
            if (this.hasNativeAppGateway()) {
                return this.sdk.nativeapp.getNotificationDeviceInfo().then(
                    deviceInfo => {
                        return {
                            subscriptionStatus: deviceInfo.status,
                            device: deviceInfo.status == SubscriptionStatus.ENABLED?notificationDeviceInfoToDeviceSubscription(deviceInfo):null,
                            listsAlsoOtherDevices: false
                        };
                    }
                );
            }
            // else if(this.supportsSafariPushNotifications()) {
            //     return Promise.reject("System not supported");
            // }
            else {
                if ( window.Notification && window.Notification.permission == "granted" ) {
                    return Promise.all([
                        this.getFirebaseToken(),
                        this.listSubscribedDevices()
                    ]).then(([
                        token,
                        devs
                    ]) => {
                        const sub = devs.subscriptions.find( e => e.token == token );
                        const others = devs.subscriptions.filter(e => e.token != token);
                        
                        const ret: SystemSubscriptionWrapper = {
                            subscriptionStatus: SubscriptionStatus.ENABLED,
                            listsAlsoOtherDevices: true,
                            otherDevices: others
                        };
                        if (sub) {
                            ret.device = sub;
                        }
                        return ret;
                    });
                }
                else if ( window.Notification && window.Notification.permission == "default" ) {
                    return Promise.resolve({
                        subscriptionStatus: SubscriptionStatus.NEVER_ASKED,
                        listsAlsoOtherDevices: false
                    });
                }
                else {
                    return Promise.resolve({
                        subscriptionStatus: SubscriptionStatus.BLOCKED,
                        listsAlsoOtherDevices: false
                    });
                }
            }
        }
        return Promise.reject("Push notifications are not supported");
    }


    subscribeThisDevice(): Promise<SystemSubscriptionWrapper> {
        return this.subscribeThisDeviceInternal().then(x => {
            this.triggerSubscriptionStatusChange(x);
            return x;
        });
    }


    private subscribeThisDeviceInternal(): Promise<SystemSubscriptionWrapper> {
        if (this.isSystemEnabled()) {
            if (this.hasNativeAppGateway()) {
               
                return this.sdk.nativeapp.getNotificationDeviceInfo().then(
                    deviceInfo => {


                        return this.sdk.registerDevice({
                            firebase: {
                                token: deviceInfo.device.token,
                                device_name: deviceInfo.device.device_name,
                                device_type: getNotificationDeviceTypeFromDetectedWebview(this.sdk.webviewDetect()),
                                device_id: deviceInfo.device.device_id
                            }
                        }).then(() => {
                            return {
                                subscriptionStatus: deviceInfo.status,
                                device: deviceInfo.status == SubscriptionStatus.ENABLED?notificationDeviceInfoToDeviceSubscription(deviceInfo):null,
                                listsAlsoOtherDevices: false
                            };
                        });
                            
                        
                        
                    }
                );
            }
            // else if(this.supportsSafariPushNotifications()) {
            //     return Promise.reject("System not supported");
            // }
            else {
                if ( window.Notification && window.Notification.permission !== "denied" ) {
                    return this.getFirebaseToken().then(
                        tok => {
                            
                            return this.sdk.retrieveOrGenerateDeviceId().then(device_id => {
                                return this.sdk.registerDevice({
                                    firebase: {
                                        token: tok,
                                        device_name: getDeviceNameFromDetectedWebview(this.sdk.webviewDetect()),
                                        device_type: getNotificationDeviceTypeFromDetectedWebview(this.sdk.webviewDetect()),
                                        device_id: device_id.deviceId
                                    }
                                });
                            }).then(r => r.data).then(
                                list => {
                                    const subscription = list.subscriptions.find(e => e.token == tok);
                                    if (subscription) {
                                        return {
                                            subscriptionStatus: SubscriptionStatus.ENABLED,
                                            device: subscription,
                                            listsAlsoOtherDevices: false
                                        };
                                    }
                                    else {
                                        return Promise.reject("Unexpected case, subscribed device is missing from response");
                                    }
                                }
                            );
                        }
                    ).catch(
                        err => {
                            this.logger.error("Error detected while subscribing", err);
                            return {
                                subscriptionStatus: SubscriptionStatus.DENIED,
                                listsAlsoOtherDevices: false
                            };
                        }
                    );
                }
                else {
                    return Promise.resolve({
                        subscriptionStatus: SubscriptionStatus.DENIED,
                        listsAlsoOtherDevices: false
                    });
                }
                
            }
        }
        return Promise.reject("Push notifications are not supported");
    }

    //? inviare l'evento di subscription status change anche qui
    //? al momento non dovrebbe essere urgente dal momento che il sistema non 
    //? consente la deregistrazione, ma al massimo la disattivazione del dispositivo
    unsubscribeThisDevice(): Promise<boolean> {
        if (this.isSystemEnabled()) {
            if (this.hasNativeAppGateway()) {
                // ? Questo caso è qui ma realisticamente parlando non dovrebbe avvenire dato che la subscription in caso di app nativa viene gestita direttamente dall'app
                return Promise.resolve(false);
            }
            // else if(this.supportsSafariPushNotifications()) {
            //     return Promise.reject("System not supported");
            // }
            else {
                if ( window.Notification && Notification.permission !== "denied" ) {
                    return Promise.all([
                        this.getFirebaseToken(),
                        this.listSubscribedDevices()
                    ]).then(([
                        tok, 
                        devices
                    ]) => {
                        const mySubscription = devices.subscriptions.find( e => e.token == tok );
                        if (mySubscription) {
                            return this.sdk.unregisterDevice({
                                notificationSystemType: NotificationSystemType.Firebase,
                                token: tok
                            }).then(_ => true );
                        }
                        else {
                            return Promise.resolve(true);
                        }
                    }).catch(
                        err => {
                            this.logger.error("Error detected while unsubscribing", err);
                            return false;
                        }
                    );
                }
                else {
                    return Promise.resolve(false);
                }
                
            }
        }
        return Promise.reject("Push notifications are not supported");
    }



    public enable(subIdentifier: SubscriptionIdentifier): Promise<SubscriptionListResponse> {
        return this.sdk.enableDevice(subIdentifier).then(
            r => r.data
        );
    }
    
    public disable(subIdentifier: SubscriptionIdentifier): Promise<SubscriptionListResponse> {
        return this.sdk.disableDevice(subIdentifier).then(
            r => r.data
        );
    }

    public listSubscribedDevices(): Promise<SubscriptionListResponse> {
        return this.sdk.listDevices().then(
            r => r.data
        );
    }

    public addListeners() {
        Promise.all([
            this.getFirebaseModule(),
            this.getServiceWorkerRegistration()
        ]).then(
            ([mod, registration]) => {
                mod.registerOnMessageFunction(
                    this.config,
                    (payload) => {

                        this.logger.debug("Foreground message received", payload);

                        const notification = payload.notification;
                        const data = payload.data;

                      
                        const options = {
                            data: {
                                url: data.modified_url
                            },
                            badge: data.badge,
                            icon: data.icon,
                            silent: true,
                            renotify: false,
                            tag: (data.breaking?"break":"news"),
                            image: data.image_url
                        };

                        this.logger.debug("Built options: ", options);

                        registration.showNotification(
                            data.title, 
                            options  
                        );
                    }
                );
            }
        );
        
    }

}